import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BUDGET_GROUPS,
  FUEL_TYPE_LABEL,
  VEHICLE_CATEGORY_LABEL,
  VEHICLE_STATUS_LABEL,
  VEHICLE_USAGE_LABEL,
  normalizePlate,
} from '@apleona/shared';
import { ApiError, api } from '../api';
import { F, FormGrid, Modal } from './Modal';

export interface VehicleFormValue {
  id?: string;
  plate: string;
  vin: string;
  make: string;
  model: string;
  category: string;
  usage: string;
  fuelType: string;
  status: string;
  budgetGroup: string;
  objectId: string;
  fuelCardNumber: string;
  companyId: string;
  locationId: string;
  currentMileage: string;
  huDueAt: string;
  leaseEndAt: string;
  notes: string;
}

export const emptyVehicle = (): VehicleFormValue => ({
  plate: '',
  vin: '',
  make: '',
  model: '',
  category: 'PKW',
  usage: 'DIENSTWAGEN',
  fuelType: 'DIESEL',
  status: 'VERFUEGBAR',
  budgetGroup: '',
  objectId: '',
  fuelCardNumber: '',
  companyId: '',
  locationId: '',
  currentMileage: '',
  huDueAt: '',
  leaseEndAt: '',
  notes: '',
});

export const vehicleToForm = (v: Record<string, unknown>): VehicleFormValue => ({
  ...emptyVehicle(),
  id: v.id as string,
  plate: (v.plate as string) ?? '',
  vin: (v.vin as string) ?? '',
  make: (v.make as string) ?? '',
  model: (v.model as string) ?? '',
  category: (v.category as string) ?? 'PKW',
  usage: (v.usage as string) ?? 'DIENSTWAGEN',
  fuelType: (v.fuelType as string) ?? 'DIESEL',
  status: (v.status as string) ?? 'VERFUEGBAR',
  budgetGroup: (v.budgetGroup as string) ?? '',
  objectId: (v.objectId as string) ?? '',
  fuelCardNumber: (v.fuelCardNumber as string) ?? '',
  companyId: ((v.company as { id?: string })?.id ?? '') as string,
  locationId: ((v.location as { id?: string })?.id ?? '') as string,
  currentMileage: v.currentMileage != null ? String(v.currentMileage) : '',
  huDueAt: v.huDueAt ? String(v.huDueAt).slice(0, 10) : '',
  leaseEndAt: v.leaseEndAt ? String(v.leaseEndAt).slice(0, 10) : '',
  notes: (v.notes as string) ?? '',
});

export const VehicleForm: React.FC<{
  open: boolean;
  initial: VehicleFormValue;
  onClose: () => void;
}> = ({ open, initial, onClose }) => {
  const qc = useQueryClient();
  const [v, setV] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setV(initial);
    setErr(null);
    setFieldErrors({});
  }, [initial, open]);

  const { data: companies } = useQuery({
    queryKey: ['gesellschaften'],
    queryFn: () => api<{ id: string; name: string }[]>('/stammdaten/gesellschaften'),
    enabled: open,
  });
  const { data: locations } = useQuery({
    queryKey: ['standorte'],
    queryFn: () => api<{ id: string; name: string; company: { id: string } }[]>('/stammdaten/standorte'),
    enabled: open,
  });

  const set = (p: Partial<VehicleFormValue>) => setV((old) => ({ ...old, ...p }));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        plate: v.plate,
        vin: v.vin || null,
        make: v.make || null,
        model: v.model || null,
        category: v.category,
        usage: v.usage,
        fuelType: v.fuelType,
        status: v.status,
        budgetGroup: v.budgetGroup || null,
        objectId: v.objectId || null,
        fuelCardNumber: v.fuelCardNumber || null,
        companyId: v.companyId || null,
        locationId: v.locationId || null,
        currentMileage: v.currentMileage ? Number(v.currentMileage) : null,
        huDueAt: v.huDueAt || null,
        leaseEndAt: v.leaseEndAt || null,
        notes: v.notes || null,
      };
      return v.id
        ? api(`/fahrzeuge/${v.id}`, { method: 'PATCH', body: JSON.stringify(body) })
        : api('/fahrzeuge', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fahrzeuge'] });
      void qc.invalidateQueries({ queryKey: ['fahrzeug'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
    },
    onError: (e: Error) => {
      setErr(e.message);
      if (e instanceof ApiError && e.details) {
        setFieldErrors(Object.fromEntries(e.details.map((d) => [d.feld, d.meldung])));
      }
    },
  });

  const locOptions = (locations ?? []).filter((l) => !v.companyId || l.company.id === v.companyId);

  return (
    <Modal
      open={open}
      title={v.id ? `Fahrzeug bearbeiten – ${v.plate}` : 'Neues Fahrzeug anlegen'}
      onClose={onClose}
      wide
      footer={
        <>
          <span className="small muted" style={{ marginRight: 'auto' }}>
            Pflichtfeld: Kennzeichen
          </span>
          <button className="btn" onClick={onClose}>Abbrechen</button>
          <button
            className="btn primary"
            disabled={v.plate.trim().length < 3 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Speichern …' : 'Speichern'}
          </button>
        </>
      }
    >
      {err ? <div className="alert error">{err}</div> : null}

      <FormGrid cols={3}>
        <div className="form-section">Fahrzeug</div>
        <F label="Kennzeichen *" hint={v.plate ? `wird gespeichert als: ${normalizePlate(v.plate)}` : 'z. B. F-AP 1234'}>
          <input
            value={v.plate}
            onChange={(e) => set({ plate: e.target.value.toUpperCase() })}
            placeholder="F-AP 1234"
            style={fieldErrors.plate ? { borderColor: 'var(--apl-red)' } : undefined}
          />
        </F>
        <F label="Marke"><input value={v.make} onChange={(e) => set({ make: e.target.value })} placeholder="z. B. VW" /></F>
        <F label="Modell"><input value={v.model} onChange={(e) => set({ model: e.target.value })} placeholder="z. B. Passat Variant" /></F>

        <F label="Fahrgestellnummer (FIN)" hint="17 Zeichen, ohne I/O/Q" span={1}>
          <input value={v.vin} onChange={(e) => set({ vin: e.target.value.toUpperCase() })} maxLength={17} />
        </F>
        <F label="Fahrzeugklasse">
          <select value={v.category} onChange={(e) => set({ category: e.target.value })}>
            {Object.entries(VEHICLE_CATEGORY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </F>
        <F label="Antrieb">
          <select value={v.fuelType} onChange={(e) => set({ fuelType: e.target.value })}>
            {Object.entries(FUEL_TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </F>

        <F label="Nutzungsart" hint="Poolfahrzeuge erscheinen im Fahrtenbuch-Ersatz">
          <select value={v.usage} onChange={(e) => set({ usage: e.target.value })}>
            {Object.entries(VEHICLE_USAGE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </F>
        <F label="Status">
          <select value={v.status} onChange={(e) => set({ status: e.target.value })}>
            {Object.entries(VEHICLE_STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </F>
        <F label="Budgetgruppe">
          <select value={v.budgetGroup} onChange={(e) => set({ budgetGroup: e.target.value })}>
            <option value="">—</option>
            {BUDGET_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </F>

        <div className="form-section">Zuordnung</div>
        <F label="Gesellschaft">
          <select
            value={v.companyId}
            onChange={(e) => set({ companyId: e.target.value, locationId: '' })}
          >
            <option value="">—</option>
            {(companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </F>
        <F label="Standort">
          <select value={v.locationId} onChange={(e) => set({ locationId: e.target.value })}>
            <option value="">—</option>
            {locOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </F>
        <F label="Kilometerstand">
          <input
            value={v.currentMileage}
            onChange={(e) => set({ currentMileage: e.target.value.replace(/\D/g, '') })}
            inputMode="numeric"
          />
        </F>

        <div className="form-section">Verwaltung</div>
        <F label="Objekt-ID"><input value={v.objectId} onChange={(e) => set({ objectId: e.target.value })} /></F>
        <F label="Tank-/Ladekarte"><input value={v.fuelCardNumber} onChange={(e) => set({ fuelCardNumber: e.target.value })} /></F>
        <F label="HU fällig am"><input type="date" value={v.huDueAt} onChange={(e) => set({ huDueAt: e.target.value })} /></F>
        <F label="Leasingende"><input type="date" value={v.leaseEndAt} onChange={(e) => set({ leaseEndAt: e.target.value })} /></F>
        <F label="Bemerkungen" span={2}>
          <textarea rows={2} value={v.notes} onChange={(e) => set({ notes: e.target.value })} />
        </F>
      </FormGrid>
    </Modal>
  );
};
